import { Button, Checkbox, Grid2, MenuItem, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { useAppContext } from "AppContext";
import { CommonPagination, GKSelect, GKTable } from "components";
import { CloseIcon } from "components/Icons";
import dayjs from "dayjs";
import useToast from "hooks/useToast";
import DashboardLayout from "layouts/DashboardLayout";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { extraColumnsField, moneyCommaSeparator } from "utils/helpers";
import { QueryKeys } from "utils/queryKey";
import CompanyLogo from "./Components/CompanyLogo";
import CreateCompanyModal from "./Components/CreateCompanyModal";

const columns: GridColDef[] = [
  {
    ...extraColumnsField("Logo", "logo", 50, 0),
    // Fixed width, just enough for the "Logo" header and the 32px image
    width: 64,
    maxWidth: 64,
    resizable: false,
    sortable: false,
    align: "center",
    headerAlign: "center",
    renderCell: (row) => <CompanyLogo logo={row?.row?.logo} />,
  },
  {
    ...extraColumnsField("Company Name", "name", 350, 1),
    renderCell: (row) => {
      const instType = row?.row?.instType;

      if (instType === "OPTIONS" || instType === "FUTURES") {
        return (
          <>
            {row?.row?.name}
            {row?.row?.expiryDate ? (
              <Typography component="span" fontWeight={600} sx={{ ml: "4px" }}>
                ({dayjs(row.row.expiryDate).format("DD-MM-YYYY")})
              </Typography>
            ) : null}
          </>
        );
      }
      return row?.row?.name;
    },
  },
  {
    ...extraColumnsField("CMP", "cmp", 120, 1),
    valueGetter: (row) => moneyCommaSeparator(Number(row.row.cmp)) || 0,
  },
  {
    ...extraColumnsField("BSE Code", "bseCode", 150, 1),
    type: "number",
  },
  {
    ...extraColumnsField("IsinCode", "isinCode", 150, 1),
  },
  {
    ...extraColumnsField("NSE Code", "nseCode", 150, 1),
  },
  {
    ...extraColumnsField("Type", "instType", 130, 1),
  },
  {
    ...extraColumnsField("Sub Type", "instSubType", 150, 1),
  },
];

const CompanyMasterMainPage = () => {
  const toast = useToast();

  const navigate = useNavigate();

  const {
    state: { commonFilter, currentUser },
    dispatch,
  } = useAppContext();

  const isDisabled = currentUser?.phone === "9911223344";

  const [openModal, setOpenModal] = useState(false);

  const instrumentFilter = commonFilter.companyMaster.instType || [];

  const filterOptions = [
    "EQ",
    "ETF",
    "MF",
    "BOND",
    "INDEX",
    "OPTIONS",
    "FUTURES",
  ];

  const requestBody = {
    requireTotalCount: true,
    take: commonFilter?.companyMaster?.take,
    skip: commonFilter?.companyMaster?.skip,
    sort: JSON.stringify(
      commonFilter?.companyMaster?.sortModel?.map((data: any) => {
        return {
          selector: data.field,
          desc: data.sort !== "asc",
        };
      })
    ),
    search: commonFilter?.companyMaster?.search || "",
    show_zero_holding: commonFilter?.companyMaster?.showNonZero,
    filter: JSON.stringify({
      ...commonFilter?.companyMaster?.customFilter,
      inst_type: instrumentFilter,
    }),
  };

  const {
    data: companyList,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: [QueryKeys.COMPANY_NAMES, requestBody],
    queryFn: () =>
      axiosInstance.get("/admin-instrument/", { params: requestBody }),
    onError: (error: any) =>
      toast.show("error", error?.message || "Something went wrong"),
    staleTime: 24 * 60 * 60 * 1000,
  });

  const cusPagination = () => {
    return (
      <CommonPagination
        name="companyMaster"
        totalCount={companyList?.data.totalCount || 0}
      />
    );
  };

  return (
    <DashboardLayout title="Company Master">
      <Grid2 container spacing={2}>
        <Grid2 size={12}>
          <GKTable
            loading={isLoading}
            columns={columns}
            rows={companyList?.data?.data || []}
            onRowClick={(row) => {
              navigate(`/app/company-master/update/${row.row.gcode}`);
            }}
            name="companyMaster"
            CustomPagination={!isLoading && cusPagination}
            headerComponent={
              <Grid2
                container
                gap={1}
                display={"flex"}
                justifyContent={"flex-end"}
              >
                <Grid2 display="flex" gap={1} alignItems="center">
                  <GKSelect
                    size="small"
                    inputLabel="Filter"
                    multiple
                    value={instrumentFilter}
                    displayEmpty
                    renderValue={(selected: unknown) =>
                      Array.isArray(selected) && selected.length > 0
                        ? selected.join(", ")
                        : ""
                    }
                    onChange={(e) => {
                      const value = e.target.value as string[];

                      dispatch({
                        type: "FILTER",
                        data: {
                          ...commonFilter,
                          companyMaster: {
                            ...commonFilter.companyMaster,
                            instType: value,
                            skip: 0,
                            page: 0,
                          },
                        },
                      });
                    }}
                    sx={{ minWidth: 140 }}
                    endAdornment={
                      instrumentFilter.length > 0 && (
                        <span
                          role="button"
                          tabIndex={0}
                          style={{
                            display: "flex",
                            cursor: "pointer",
                            alignItems: "center",
                            paddingRight: 8,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch({
                              type: "FILTER",
                              data: {
                                ...commonFilter,
                                companyMaster: {
                                  ...commonFilter.companyMaster,
                                  instType: [],
                                  skip: 0,
                                  page: 0,
                                },
                              },
                            });
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.stopPropagation();
                              dispatch({
                                type: "FILTER",
                                data: {
                                  ...commonFilter,
                                  companyMaster: {
                                    ...commonFilter.companyMaster,
                                    instType: [],
                                    skip: 0,
                                    page: 0,
                                  },
                                },
                              });
                            }
                          }}
                        >
                          <CloseIcon />
                        </span>
                      )
                    }
                  >
                    {filterOptions.map((item) => {
                      const checked = instrumentFilter.includes(item);

                      return (
                        <MenuItem
                          key={item}
                          value={item}
                          onClick={() => {
                            const updated = checked
                              ? instrumentFilter.filter((x: any) => x !== item)
                              : [...instrumentFilter, item];

                            dispatch({
                              type: "FILTER",
                              data: {
                                ...commonFilter,
                                companyMaster: {
                                  ...commonFilter.companyMaster,
                                  instType: updated,
                                  skip: 0,
                                  page: 0,
                                },
                              },
                            });
                          }}
                        >
                          <Checkbox checked={checked} size="small" />
                          <span>{item}</span>
                        </MenuItem>
                      );
                    })}
                  </GKSelect>
                </Grid2>
                <Grid2 display="flex" gap={1}>
                  <Button
                    variant="contained"
                    onClick={() =>
                      navigate("/app/company-master/unique-company")
                    }
                  >
                    Unique company
                  </Button>
                  <Button
                    variant="contained"
                    disabled={isDisabled}
                    onClick={() => setOpenModal(true)}
                  >
                    Add Company
                  </Button>
                </Grid2>
              </Grid2>
            }
          />
        </Grid2>
      </Grid2>

      {openModal && (
        <CreateCompanyModal
          isModalOpen={openModal}
          setIsModalOpen={setOpenModal}
          refetch={refetch}
        />
      )}
    </DashboardLayout>
  );
};

export default CompanyMasterMainPage;
